import { UseQueryResult } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import useQuery from '@/api/queries/useQuery';

import { FILTER_CONFIG, FilterType } from '@/models';
import { FilterState, isDynamicFilter, isStaticFilter } from '@/types/filter';
import { SelectItem } from '@/types/ui';

import { CubeRowData } from '@/api/hcp/interaction/types';
import { useAuthContext } from '@/context/AuthContext';
import { primeUserCountriesCache, syncCachedCountryCodes } from '@/models/filter/configs/general/countryFilter';
import { isStringArray } from '@/api/queries/utils';
import { useFiltersContext } from '@/context/FiltersContext/FiltersContextProvider';
import { isPeriod } from '@/utils/date/isPeriod';
import { translateDynamicKey } from '@/types/translation';
import { useGetGlobalFilters } from '@/utils/cube';
import { useTranslation } from 'react-i18next';
import { useGetUserCountries } from '../country';
import { FilterKeys } from './cacheKeys';
import { mapDynamicFilterOptions } from './mapDynamicFilterOptions';
import { useOptionScopeFilters } from './optionScope';

type UseGetFilterOptionsRequest = {
  type: FilterType;
  applyDynamicFilters?: boolean;
  isSecondarySegment?: boolean;
  enabled?: boolean;
};

export function useGetFilterOptions<TData = SelectItem[], TError = AxiosError>({
  type,
  applyDynamicFilters = true,
  isSecondarySegment,
  enabled = true,
}: UseGetFilterOptionsRequest): UseQueryResult<TData, TError> {
  const { t } = useTranslation();
  const { watch, getValues } = useFormContext<FilterState>() || {};
  const [formState, setFormState] = useState<FilterState>({});
  const { data: userCountries, isLoading } = useGetUserCountries();

  const currentFormState = getValues?.() ?? {};

  const { filterState } = useFiltersContext();
  const dateRange = formState[FilterType.DATE_RANGE] ?? filterState[FilterType.DATE_RANGE];

  // Keep country-code resolution ready for every dynamic options query. The
  // country dropdown normally warms these caches as a side effect of rendering,
  // but it is unmounted on pages that render other filters directly (e.g. Channel
  // Focus channel-specific filters). Without this, `applyFilter` cannot resolve
  // `hcp.country_code`, so global-country cascading stops narrowing those queries
  // and stale selections never appear disabled.
  //
  // Priming must run synchronously before `useGetGlobalFilters` builds the query
  // below (it reads the resolved codes via `applyFilter`), and only when its
  // inputs change so the sessionStorage sync event is not fired on every render.
  // A ref guard (rather than useMemo) keeps the side effect off the memo cache,
  // which React may discard or double-invoke.
  const countryValue = filterState[FilterType.COUNTRY];
  const primingInputsRef = useRef<{
    userCountries: typeof userCountries;
    countryValue: typeof countryValue;
  } | null>(null);
  if (
    primingInputsRef.current?.userCountries !== userCountries ||
    primingInputsRef.current?.countryValue !== countryValue
  ) {
    primingInputsRef.current = { userCountries, countryValue };
    primeUserCountriesCache(userCountries);
    if (isStringArray(countryValue) && countryValue.length) {
      syncCachedCountryCodes(countryValue);
    }
  }

  // Option queries must not exclude disabled values: they populate the
  // availability registry that the exclusion reads, which would otherwise loop.
  const globalFilters = useGetGlobalFilters(currentFormState, type, isSecondarySegment, false);
  const optionScopeFilters = useOptionScopeFilters();

  useEffect(() => {
    if (!watch) return;
    const subscription = watch(state => setFormState(state as FilterState));
    return () => subscription.unsubscribe();
  }, [watch]);

  const filterConfig = useMemo(() => FILTER_CONFIG[type], [type]);

  const dynamicConfig = isDynamicFilter(filterConfig) ? filterConfig : undefined;
  const isDynamic = dynamicConfig !== undefined;
  const isStatic = isStaticFilter(filterConfig);

  const { userInfo } = useAuthContext();

  const cubeQuery = useMemo(() => {
    if (!dynamicConfig || !isPeriod(dateRange)) return undefined;
    const query = dynamicConfig.getOptionsQuery({
      sourceCube: dynamicConfig.sourceCube,
      dateRange,
    });
    return {
      ...query,
      limit: query.limit,
      filters: [...(query.filters ?? []), ...optionScopeFilters, applyDynamicFilters ? globalFilters : null],
    };
  }, [dynamicConfig, globalFilters, dateRange, applyDynamicFilters, optionScopeFilters]);

  const queryKey = isDynamic ? [...FilterKeys.filter(type, cubeQuery, isSecondarySegment)] : undefined;
  const query = useQuery<CubeRowData[], TError, TData>({
    payload: cubeQuery,
    options: {
      queryKey,
      select: data => {
        if (!dynamicConfig) return data as TData;
        return mapDynamicFilterOptions(dynamicConfig, data, userInfo) as TData;
      },
      enabled: isDynamic && enabled && cubeQuery !== undefined,
    },
  });

  const result = useMemo(() => {
    let options = [] as TData;

    if (isStatic) {
      if (typeof filterConfig.options === 'function') {
        options = filterConfig.options({ contexts: { userCountries } }) as TData;
      } else {
        options = filterConfig.options.map(option => {
          if (typeof option.label === 'string') return { ...option, label: translateDynamicKey(t, option.label) };
          return option;
        }) as TData;
      }
    }

    if (isDynamic) {
      options = query.data;
    }
    return { data: options, isLoading: query.isLoading || isLoading || false } as UseQueryResult<TData, TError>;
  }, [t, query, isStatic, isDynamic, filterConfig, isLoading, userCountries]);

  return result;
}
